import React from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PriceCacheProvider } from "@/contexts/PriceCacheContext";
import Landing from "./pages/Landing";

const Index = React.lazy(() => import("./pages/Index"));
const Login = React.lazy(() => import("./pages/Login"));
const About = React.lazy(() => import("./pages/About"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Settings = React.lazy(() => import("./pages/Settings"));
const MealPlans = React.lazy(() => import("./pages/MealPlans"));
const CustomMealPlans = React.lazy(() => import("./pages/CustomMealPlans"));
const SubscriptionSuccess = React.lazy(() => import("./pages/subscription/Success"));
const SubscriptionCancel = React.lazy(() => import("./pages/subscription/Cancel"));
const LoadMealPlan = React.lazy(() => import("./pages/LoadMealPlan"));
import { useEffect } from "react";
import { supabase } from "./integrations/supabase/client";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAuthCallback = async () => {
      const params = new URLSearchParams(location.search);
      if (params.has('code') || params.has('access_token')) {
        console.log('Auth callback detected, handling session...');
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) throw error;
          
          if (session) {
            console.log('Session obtained, redirecting to /recipes');
            navigate('/recipes', { replace: true });
          }
        } catch (error) {
          console.error('Error in auth callback:', error);
          navigate('/login', { replace: true });
        }
      }
    };

    handleAuthCallback();
  }, [location, navigate]);

  return null;
}

const AppContent = () => {
  return (
    <React.Suspense fallback={<div className="min-h-screen bg-background" />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/recipes" element={<Index />} />
      <Route path="/custom" element={<CustomMealPlans />} />
      <Route path="/login" element={<Login />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/meal-plans" element={<MealPlans />} />
      <Route path="/meal-plans/:slug" element={<LoadMealPlan />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
    </Routes>
    </React.Suspense>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <PriceCacheProvider>
            <TooltipProvider>
              <AuthCallback />
              <AppContent />
            </TooltipProvider>
          </PriceCacheProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;